import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Filter, AlertCircle, ArrowLeft } from 'lucide-react';
import { Lot, Model, Elevation, ExteriorPalette, FilterState } from '../../types';
import { lots, models, exteriorPalettes } from '../../data/mockData';
import { LotOverlay } from '../shared/LotOverlay';
import { ModelCard } from '../shared/ModelCard';
import { LotFilters } from '../Filters/LotFilters';
import { LotPreviewCard } from '../Panels/LotPreviewCard';
import { TopNav } from '../shared/TopNav';
import { MainMenuPanel } from '../Panels/MainMenuPanel';
interface LotsModeProps {
  onCustomizingChange?: (isCustomizing: boolean) => void;
  onModeReselect?: () => void;
  initialLotId?: number | null;
  isCustomizingInitial?: boolean;
}
export function LotsMode({
  onCustomizingChange,
  onModeReselect,
  initialLotId = null,
  isCustomizingInitial = false
}: LotsModeProps) {
  // State
  const [selectedLotId, setSelectedLotId] = useState<number | null>(initialLotId);
  const [isCustomizing, setIsCustomizing] = useState(isCustomizingInitial);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [selectedElevationId, setSelectedElevationId] = useState<string | null>(null);
  const [selectedPaletteId, setSelectedPaletteId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [cameraDirection] = useState<'N' | 'S' | 'E' | 'W' | 'NE' | 'NW' | 'SE' | 'SW'>('N');
  const [filters, setFilters] = useState<FilterState>({
    blocks: new Set(['Block 1', 'Block 2', 'Block 3']),
    availability: new Set(['available', 'pending', 'sold']),
    priceRange: [100000, 2500000],
    sqftRange: [1500, 4000],
    landSqftRange: [5000, 15000],
    beds: [4, 6],
    baths: [2.5, 4],
    facing: new Set(['N', 'E', 'S', 'W']),
    possessionDate: new Set(['immediate', 'over90'])
  });
  // Update state when props change (for URL routing)
  useEffect(() => {
    if (initialLotId !== null && initialLotId !== selectedLotId) {
      setSelectedLotId(initialLotId);
    }
    if (isCustomizingInitial !== isCustomizing) {
      setIsCustomizing(isCustomizingInitial);
      onCustomizingChange?.(isCustomizingInitial);
    }
  }, [initialLotId, isCustomizingInitial]);
  // Listen for mode reselect to reopen filters
  useEffect(() => {
    if (onModeReselect) {
      const handleReselect = () => {
        setShowFilters(true);
      };
      // This will be called from parent when Lots is clicked again
      window.addEventListener('lotsmode-reselect', handleReselect as any);
      return () => window.removeEventListener('lotsmode-reselect', handleReselect as any);
    }
  }, [onModeReselect]);
  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCustomizing) {
          // Close customization panel first
          handleBackToPreview();
        } else if (selectedLotId) {
          // Close lot preview
          handleCloseLot();
        } else if (showFilters) {
          // Close filters panel last
          setShowFilters(false);
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isCustomizing, selectedLotId, showFilters]);
  // Derived Data
  const selectedLot = lots.find(l => l.id === selectedLotId);
  const compatibleModels = selectedLot ? models.filter(m => m.compatibleLots.includes(selectedLot.id)) : [];
  // Filter Logic
  const filteredLots = lots.filter(lot => {
    if (filters.blocks.size > 0 && !filters.blocks.has(lot.block)) return false;
    if (filters.availability.size > 0 && !filters.availability.has(lot.status)) return false;
    if (filters.facing.size > 0 && !filters.facing.has(lot.facing)) return false;
    return true;
  });
  // Handlers
  const handleLotSelect = (lot: Lot) => {
    // Only allow selecting available lots
    if (lot.status === 'sold') return;
    setSelectedLotId(lot.id);
    setIsCustomizing(false);
    setSelectedModelId(null);
    setSelectedElevationId(null);
    setSelectedPaletteId(null);
    onCustomizingChange?.(false);
    // Update URL
    window.history.pushState({}, '', `/lots/${lot.id}`);
  };
  const handleCustomize = () => {
    setIsCustomizing(true);
    onCustomizingChange?.(true);
    // Update URL
    if (selectedLotId) {
      window.history.pushState({}, '', `/lots/${selectedLotId}/customize`);
    }
  };
  const handleBackToPreview = () => {
    setIsCustomizing(false);
    setSelectedModelId(null);
    setSelectedElevationId(null);
    setSelectedPaletteId(null);
    onCustomizingChange?.(false);
    // Update URL
    if (selectedLotId) {
      window.history.pushState({}, '', `/lots/${selectedLotId}`);
    }
  };
  const handleCloseLot = () => {
    setSelectedLotId(null);
    setIsCustomizing(false);
    setSelectedModelId(null);
    setSelectedElevationId(null);
    setSelectedPaletteId(null);
    onCustomizingChange?.(false);
    // Update URL
    window.history.pushState({}, '', '/lots');
  };
  const handleRecenter = () => {
    // TODO: Implement camera recenter to selected lot
    console.log('Recenter camera to lot', selectedLotId);
  };
  const handle360View = () => {
    // TODO: Implement 360° view
    console.log('Launch 360° view for lot', selectedLotId);
  };
  const handleVisit = () => {
    // TODO: Implement interior visit
    console.log('Launch interior visit for lot', selectedLotId);
  };
  const handleModelSelect = (model: Model) => {
    setSelectedModelId(model.id);
    setSelectedElevationId(model.elevations[0].id);
    setSelectedPaletteId(null);
  };
  const handleElevationSelect = (elevation: Elevation) => {
    setSelectedElevationId(elevation.id);
    setSelectedPaletteId(null);
  };
  const handlePaletteSelect = (palette: ExteriorPalette) => {
    setSelectedPaletteId(palette.id);
  };
  // Business Rule: Adjacent Lot Validation (Mock)
  const isOptionDisabled = (modelId: string, elevationId?: string) => {
    // Mock rule: If lot 2 is selected, Model 1 Elevation A is disabled due to neighbor
    if (selectedLotId === 2 && modelId === 'm1' && elevationId === 'e1a') return true;
    return false;
  };
  return <>
      <TopNav onMenuClick={() => setShowMenu(true)} cameraDirection={cameraDirection} data-id="element-682" />

      {/* Main Menu Panel */}
      <MainMenuPanel isOpen={showMenu} onClose={() => setShowMenu(false)} onOpenControls={() => console.log('Open Controls')} onOpenMediaGallery={() => console.log('Open Media Gallery')} onOpenSettings={() => console.log('Open Settings')} data-id="element-683" />

      {/* Left Sidebar Filters - Collapse when customizing */}
      <AnimatePresence data-id="element-684">
        {!isCustomizing && showFilters && <motion.div initial={{
        x: 0
      }} exit={{
        x: -400
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} data-id="element-685">
            <LotFilters filters={filters} onFilterChange={setFilters} onClose={() => setShowFilters(false)} data-id="element-686" />
          </motion.div>}
      </AnimatePresence>

      {/* 3D Scene Lot Overlays */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-687">
        <div className="relative w-full h-full pointer-events-auto" data-id="element-688">
          {filteredLots.map(lot => <LotOverlay key={lot.id} lot={lot} isSelected={selectedLotId === lot.id} onSelect={handleLotSelect} data-id="element-689" />)}
        </div>
      </div>

      {/* Simulated 3D House Placement */}
      <AnimatePresence data-id="element-690">
        {selectedLot && selectedModelId && selectedElevationId && <motion.div initial={{
        opacity: 0,
        scale: 0.8,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} className="absolute z-20 pointer-events-none transform -translate-x-1/2 -translate-y-full" style={{
        left: `${selectedLot.coordinates.x}%`,
        top: `${selectedLot.coordinates.y - 5}%`
      }} data-id="element-691">
            <div className="relative" data-id="element-692">
              <img src={models.find(m => m.id === selectedModelId)?.elevations.find(e => e.id === selectedElevationId)?.thumbnailUrl} alt="3D Preview" className="w-48 h-auto rounded-lg shadow-2xl border-2 border-white/20" style={{
            filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))'
          }} data-id="element-693" />
              {selectedPaletteId && <div className="absolute top-2 right-2 w-6 h-6 rounded-full border-2 border-white shadow-lg" style={{
            backgroundImage: `url(${exteriorPalettes.find(p => p.id === selectedPaletteId)?.thumbnailUrl})`,
            backgroundSize: 'cover'
          }} data-id="element-694" />}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lot Preview Card (Step 1) */}
      <AnimatePresence data-id="element-695">
        {selectedLot && !isCustomizing && <LotPreviewCard lot={selectedLot} onClose={handleCloseLot} onCustomize={handleCustomize} onRecenter={handleRecenter} on360View={handle360View} onVisit={handleVisit} data-id="element-696" />}
      </AnimatePresence>

      {/* Right Configuration Panel (Step 2 - Full Customization) */}
      <AnimatePresence data-id="element-697">
        {selectedLotId && isCustomizing && <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="absolute top-0 right-0 bottom-0 w-[420px] z-[90] bg-slate-900/95 border-l border-white/10 backdrop-blur-xl shadow-2xl flex flex-col" data-id="element-698">
            {/* Header */}
            <div className="p-6 border-b border-white/10 bg-slate-900/50" data-id="element-699">
              <div className="flex items-center justify-between mb-2" data-id="element-700">
                <div className="flex items-center gap-3" data-id="element-701">
                  <button onClick={handleBackToPreview} className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" aria-label="Back to lot preview" data-id="element-702">
                    <ArrowLeft className="w-5 h-5" data-id="element-703" />
                  </button>
                  <div data-id="element-704">
                    <h2 className="text-2xl font-light text-white" data-id="element-705">
                      Lot{' '}
                      <span className="font-bold text-cyan-400" data-id="element-706">
                        {selectedLotId}
                      </span>
                    </h2>
                    <p className="text-xs text-slate-400" data-id="element-707">
                      {selectedLot?.block} •{' '}
                      {selectedLot?.sqft.toLocaleString()} sq ft •{' '}
                      {selectedLot?.facing}-Facing
                    </p>
                  </div>
                </div>
                <button onClick={handleCloseLot} className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" aria-label="Close" data-id="element-708">
                  <X className="w-5 h-5" data-id="element-709" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar" data-id="element-710">
              {/* Models List */}
              <div className="space-y-4" data-id="element-711">
                {compatibleModels.map((model, index) => {
              const isDisabled = isOptionDisabled(model.id);
              const isSelected = selectedModelId === model.id;
              return <motion.div key={model.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: isDisabled ? 0.5 : 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="relative" data-id="element-712">
                      <ModelCard model={model} selectedElevationId={isSelected ? selectedElevationId || undefined : undefined} selectedPaletteId={isSelected ? selectedPaletteId || undefined : undefined} palettes={isSelected && selectedElevationId ? exteriorPalettes : undefined} onSelectModel={handleModelSelect} onSelectElevation={handleElevationSelect} onSelectPalette={isSelected && selectedElevationId ? handlePaletteSelect : undefined} layout="vertical" data-id="element-713" />

                      {/* Disabled Overlay */}
                      {isDisabled && <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10 border border-white/5" data-id="element-714">
                          <div className="bg-slate-900 px-4 py-2 rounded-lg border border-red-500/30 flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-wide shadow-xl" data-id="element-715">
                            <AlertCircle className="w-4 h-4" data-id="element-716" />
                            Unavailable
                          </div>
                        </div>}
                    </motion.div>;
            })}
              </div>

              <div className="h-20" data-id="element-717" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}